import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { postContact } from "../lib/api";

const MICHAEL_PORTRAIT =
  "https://customer-assets.emergentagent.com/wingman/2aaf26c1-2a07-4709-9e4e-abab64ebc2fe/attachments/8e62811134784c19a4cca3b437c0aa03_image.bin";
const NANCY_PORTRAIT =
  "https://customer-assets.emergentagent.com/wingman/2aaf26c1-2a07-4709-9e4e-abab64ebc2fe/attachments/fa2a9169548f414081957c8d888ee22a_image.bin";

// Localized labels for the dual-contact cards
const CARD_COPY = {
  nl: {
    overline: "Persoonlijk contact",
    title: "Spreek direct met Michael of Nancy.",
    intro:
      "Kies wie u het beste kan helpen — beiden Nederlandstalig, beschikbaar via telefoon en WhatsApp.",
    michaelRole: "Founder · NM Luxury Estates",
    nancyRole: "Co-owner · NM Luxury Estates",
    callLabel: "Bel direct",
    whatsappLabel: "WhatsApp",
    emailLabel: "E-mail",
    michaelMsg: "Hallo Michael, ik heb een vraag over een woning aan de Costa del Sol.",
    nancyMsg: "Hallo Nancy, ik heb een vraag over een woning aan de Costa del Sol.",
  },
  en: {
    overline: "Direct contact",
    title: "Speak directly with Michael or Nancy.",
    intro:
      "Choose who can best assist you — both Dutch-speaking and reachable by phone and WhatsApp.",
    michaelRole: "Founder · NM Luxury Estates",
    nancyRole: "Co-owner · NM Luxury Estates",
    callLabel: "Call now",
    whatsappLabel: "WhatsApp",
    emailLabel: "Email",
    michaelMsg: "Hello Michael, I have a question about a property on the Costa del Sol.",
    nancyMsg: "Hello Nancy, I have a question about a property on the Costa del Sol.",
  },
  de: {
    overline: "Persönlicher Kontakt",
    title: "Sprechen Sie direkt mit Michael oder Nancy.",
    intro:
      "Wählen Sie, wer Ihnen am besten weiterhelfen kann — beide niederländischsprachig, erreichbar per Telefon und WhatsApp.",
    michaelRole: "Founder · NM Luxury Estates",
    nancyRole: "Co-owner · NM Luxury Estates",
    callLabel: "Jetzt anrufen",
    whatsappLabel: "WhatsApp",
    emailLabel: "E-Mail",
    michaelMsg: "Hallo Michael, ich habe eine Frage zu einer Immobilie an der Costa del Sol.",
    nancyMsg: "Hallo Nancy, ich habe eine Frage zu einer Immobilie an der Costa del Sol.",
  },
  es: {
    overline: "Contacto directo",
    title: "Hable directamente con Michael o Nancy.",
    intro:
      "Elija con quién prefiere comunicarse — ambos disponibles por teléfono y WhatsApp.",
    michaelRole: "Founder · NM Luxury Estates",
    nancyRole: "Co-owner · NM Luxury Estates",
    callLabel: "Llamar ahora",
    whatsappLabel: "WhatsApp",
    emailLabel: "Email",
    michaelMsg: "Hola Michael, tengo una consulta sobre una propiedad en la Costa del Sol.",
    nancyMsg: "Hola Nancy, tengo una consulta sobre una propiedad en la Costa del Sol.",
  },
};

function ContactCard({ name, role, photo, phone, phoneDisplay, email, waNumber, waMessage, copy, testIdPrefix }) {
  const waHref = `https://wa.me/${waNumber}?text=${encodeURIComponent(waMessage)}`;
  return (
    <article
      className="flex flex-col border border-cream/10 hover:border-sky-300/40 bg-charcoal-surface transition-colors"
      data-testid={`${testIdPrefix}-card`}
    >
      {/* Portrait */}
      <div className="relative aspect-[4/5] overflow-hidden bg-charcoal">
        <img
          src={photo}
          alt={name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {/* Subtle sky accent ribbon on top edge */}
        <span
          aria-hidden="true"
          className="absolute top-0 left-0 h-[2px] w-16"
          style={{ background: "#7BB3D1" }}
        />
      </div>

      {/* Identity + actions */}
      <div className="flex flex-col gap-6 p-8 md:p-10 flex-1">
        <header>
          <h3 className="font-serif text-3xl md:text-4xl text-cream leading-tight" data-testid={`${testIdPrefix}-name`}>
            {name}
          </h3>
          <p className="text-[0.7rem] tracking-[0.24em] uppercase font-light mt-3" style={{ color: "#7BB3D1" }}>
            {role}
          </p>
        </header>

        <div className="flex flex-col gap-3 text-sm">
          <a
            href={`tel:${phone}`}
            className="flex items-center gap-3 text-cream hover:text-gold transition-colors"
            data-testid={`${testIdPrefix}-call`}
          >
            <Phone size={15} strokeWidth={1.5} className="text-gold shrink-0" />
            <span className="tracking-wide">{phoneDisplay}</span>
          </a>
          <a
            href={`mailto:${email}`}
            className="flex items-center gap-3 text-cream-muted hover:text-gold transition-colors break-all"
            data-testid={`${testIdPrefix}-email`}
          >
            <Mail size={15} strokeWidth={1.5} className="text-gold shrink-0" />
            <span>{email}</span>
          </a>
        </div>

        <div className="mt-auto pt-2 flex flex-col sm:flex-row gap-3">
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 text-[0.74rem] tracking-[0.22em] uppercase font-light text-white transition-colors"
            style={{ backgroundColor: "#25D366" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#1ebd5b")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#25D366")}
            data-testid={`${testIdPrefix}-whatsapp`}
            aria-label={`WhatsApp ${name}`}
          >
            <MessageCircle size={15} strokeWidth={1.5} />
            {copy.whatsappLabel} {name.split(" ")[0]}
          </a>
          <a
            href={`tel:${phone}`}
            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 text-[0.74rem] tracking-[0.22em] uppercase font-light border border-gold text-gold hover:bg-gold/10 transition-colors"
            data-testid={`${testIdPrefix}-call-cta`}
          >
            <Phone size={14} strokeWidth={1.5} />
            {copy.callLabel}
          </a>
        </div>
      </div>
    </article>
  );
}

export default function Contact() {
  const { t, lang } = useLang();
  const copy = CARD_COPY[lang] || CARD_COPY.nl;
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [state, setState] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await postContact({ ...form, source: "contact_page" });
      setState("ok");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      setState("error");
    }
  };

  return (
    <div className="bg-charcoal pt-32 pb-24" data-testid="page-contact">
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
        {/* Page header */}
        <div className="mb-16 md:mb-20">
          <p className="overline-sky" data-testid="contact-overline">
            {t.contact.overline}
          </p>
          <h1 className="font-serif text-4xl md:text-6xl text-cream leading-[1.05] max-w-3xl mt-5">
            {t.contact.title}
          </h1>
          <p className="text-cream-muted text-base mt-8 max-w-2xl leading-relaxed">
            {t.contact.intro}
          </p>
        </div>

        {/* DUAL CONTACT CARDS — Michael + Nancy */}
        <section className="mb-20 md:mb-28" data-testid="contact-direct">
          <div className="mb-12 max-w-2xl">
            <p className="overline-sky">{copy.overline}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-cream leading-tight mt-4">
              {copy.title}
            </h2>
            <p className="text-cream-muted text-sm md:text-base mt-5 leading-relaxed">
              {copy.intro}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            <ContactCard
              name="Michael Hannes"
              role={copy.michaelRole}
              photo={MICHAEL_PORTRAIT}
              phone="+34604134186"
              phoneDisplay="+34 604 134 186"
              email="michael@nmluxuryestates.com"
              waNumber="34604134186"
              waMessage={copy.michaelMsg}
              copy={copy}
              testIdPrefix="contact-michael"
            />
            <ContactCard
              name="Nancy Hannes"
              role={copy.nancyRole}
              photo={NANCY_PORTRAIT}
              phone="+34613905797"
              phoneDisplay="+34 613 905 797"
              email="nancy@nmluxuryestates.com"
              waNumber="34613905797"
              waMessage={copy.nancyMsg}
              copy={copy}
              testIdPrefix="contact-nancy"
            />
          </div>
        </section>

        {/* FORM + OFFICE INFO */}
        <div className="grid lg:grid-cols-5 gap-12">
          {/* Form */}
          <div className="lg:col-span-3 border border-cream/10 p-8 md:p-12 bg-charcoal-surface">
            <h2 className="font-serif text-3xl text-cream">{t.contact.formTitle}</h2>
            <p className="overline-sky mt-3 mb-8">Formulier</p>
            {state === "ok" ? (
              <div className="border border-gold/40 p-6 text-gold text-sm" data-testid="contact-success">
                {t.contact.success}
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-4">
                  <input
                    required
                    placeholder={t.contact.name}
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="input-luxury"
                    data-testid="contact-name"
                  />
                  <input
                    required
                    type="email"
                    placeholder={t.contact.email}
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    className="input-luxury"
                    data-testid="contact-email"
                  />
                </div>
                <input
                  placeholder={t.contact.phone}
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  className="input-luxury"
                  data-testid="contact-phone"
                />
                <textarea
                  required
                  rows={6}
                  placeholder={t.contact.message}
                  value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  className="input-luxury resize-none"
                  data-testid="contact-message"
                />
                {state === "error" && (
                  <p className="text-red-400 text-xs">{t.contact.error}</p>
                )}
                <button
                  type="submit"
                  disabled={state === "sending"}
                  className="btn-gold-solid disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {t.contact.submit}
                </button>
              </form>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-2 space-y-8">
            <div className="border border-cream/10 p-8">
              <h3 className="font-serif text-2xl text-cream">{t.contact.officeTitle}</h3>
              <p className="overline-sky mt-3 mb-6">Adres</p>
              <div className="flex items-start gap-3 text-cream">
                <MapPin size={18} strokeWidth={1.25} className="text-gold mt-1 shrink-0" />
                <div>
                  <p className="font-serif text-lg leading-tight">NM Luxury Estates</p>
                  <p className="text-cream-muted text-sm mt-1">
                    Vista Bahia 2/40<br />
                    29690 Casares del Sol<br />
                    Spanje
                  </p>
                </div>
              </div>
            </div>

            <div className="border border-cream/10 p-8">
              <h3 className="font-serif text-2xl text-cream">{t.contact.hoursLabel}</h3>
              <p className="overline-sky mt-3 mb-6">Openingstijden</p>
              <div className="flex items-start gap-3 text-cream-muted text-sm whitespace-pre-line">
                <Clock size={16} strokeWidth={1.25} className="text-gold mt-1 shrink-0" />
                <p>{t.contact.hours}</p>
              </div>
            </div>

            {/* Map iframe */}
            <div className="aspect-[4/3] border border-cream/10 bg-charcoal-surface">
              <iframe
                title="Map"
                src="https://www.google.com/maps?q=Bahia+Casares+Spain&output=embed"
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
